// FILE: PullRequestActions.tsx
// Purpose: The four writes a reviewer makes from the panel — merge, close, reopen, ready — plus
//          opening a thread on the pull request.
// Layer: Component
// Exports: PullRequestActions
//
// Which buttons exist is derived from the pull request's own state rather than from a fixed row
// with things greyed out: there is no such thing as reopening an open pull request, and a button
// that can never apply is a question the reader has to answer every time they look at it.
//
// Merge and close ask first. Neither is reversible in any way the reader would recognise — a
// merge writes to the base branch and a close hides the pull request from everyone's list — and
// both sit one careless click away from "Ready for review". Reopen and ready do not ask: they are
// each other's undo.
//
// "Crear hilo" sits with them because this is where the reader's hands are, but it is not one of
// them: it writes nothing to GitHub, so it asks for `p.thread.create` rather than `p.git_write`,
// and it is deliberately absent on a merged or closed pull request — the same rule the picker in
// the New thread dialog follows, and a second criterion would be a puzzle rather than a feature.

import type {
  PullRequestDetail,
  PullRequestMergeMethod,
  PullRequestRef,
  PullRequestRunActionInput,
} from "@taskin-code/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { toastManager } from "~/components/ui/toast";
import { pullRequestsRunActionMutationOptions } from "~/lib/pullRequestsReactQuery";
import { useProjectUiCapabilities } from "~/auth/projectUiCapabilities";
import { openNewThreadDialog } from "~/newThreadDialogStore";
import { useStore } from "~/store";
import { readPullRequestErrorMessage } from "./pullRequestPresentation";
import { PullRequestQaActions } from "./PullRequestQaActions";

/** `undefined` means "whatever the repository is configured to do", which is what GitHub applies
 * when the call names no method. Its own option rather than a guess at the repository's default:
 * this fork does not read repository settings, so guessing would sometimes be wrong silently. */
const MERGE_METHOD_VALUES = ["default", "merge", "squash", "rebase"] as const;
type MergeMethodValue = (typeof MERGE_METHOD_VALUES)[number];

const MERGE_METHOD_LABELS: Record<MergeMethodValue, string> = {
  default: "Repository default",
  merge: "Merge commit",
  squash: "Squash and merge",
  rebase: "Rebase and merge",
};

type PendingConfirmation = { action: "merge" | "close"; title: string; description: string } | null;

export function PullRequestActions({
  reference,
  detail,
}: {
  reference: PullRequestRef;
  detail: PullRequestDetail;
}) {
  const queryClient = useQueryClient();
  const { canCreateThread } = useProjectUiCapabilities(reference.projectId);
  const threads = useStore((store) => store.threads);
  const [mergeMethod, setMergeMethod] = useState<MergeMethodValue>("default");
  const [pending, setPending] = useState<PendingConfirmation>(null);

  const runAction = useMutation(pullRequestsRunActionMutationOptions({ queryClient }));

  // One flag for every button: the guard against a double click is that nothing is clickable
  // while a write is in flight, not that each button watches itself.
  const isBusy = runAction.isPending;

  const run = (action: PullRequestRunActionInput["action"]) => {
    runAction.mutate(
      {
        ...reference,
        action,
        ...(action === "merge" && mergeMethod !== "default"
          ? { mergeMethod: mergeMethod as PullRequestMergeMethod }
          : {}),
      },
      {
        // Per call rather than on the mutation: the options builder owns cache invalidation, and
        // telling somebody their merge failed is this component's business, not the cache's.
        onError: (error) => {
          toastManager.add({
            type: "error",
            title: `Could not ${action} #${reference.number}`,
            description: readPullRequestErrorMessage(error, "GitHub refused the request."),
          });
        },
      },
    );
  };

  const confirmAndRun = () => {
    const action = pending?.action;
    setPending(null);
    if (action) run(action);
  };

  /**
   * The thread this pull request already has, if any. Opening a second one is allowed — a fresh
   * start after abandoning the first is a real thing — so this only changes what the button says,
   * never whether it works.
   */
  const existingThread =
    threads.find(
      (thread) =>
        thread.linkedPullRequest?.projectId === reference.projectId &&
        thread.linkedPullRequest.number === detail.number,
    ) ?? null;

  const openThreadDialog = () => {
    openNewThreadDialog({
      preferredProjectId: reference.projectId,
      pullRequest: {
        projectId: reference.projectId,
        number: detail.number,
        headBranch: detail.headBranch,
        title: detail.title,
      },
    });
  };

  const showCreateThread = detail.state === "open" && canCreateThread;

  const canMerge = detail.state === "open" && !detail.isDraft;
  const canReady = detail.state === "open" && detail.isDraft;
  const canClose = detail.state === "open";
  const canReopen = detail.state === "closed";

  // A merged pull request keeps its trial buttons, unlike "Crear hilo": the
  // environment may still be wearing it, and the only thing that takes it off
  // is «Volver a la base». Hiding them here would strand a shared workspace on
  // a branch that has since landed, with no control anywhere to clear it.
  if (detail.state === "merged") {
    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-[11px] text-muted-foreground">
        <span>
          Merged{detail.mergedAt ? ` on ${new Date(detail.mergedAt).toLocaleString()}` : ""}.
        </span>
        <PullRequestQaActions projectId={reference.projectId} prNumber={detail.number} />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-t border-border/60 px-3 py-2.5">
        {showCreateThread ? (
          <Button size="xs" variant="outline" disabled={isBusy} onClick={openThreadDialog}>
            {existingThread ? "Crear otro hilo" : "Crear hilo"}
          </Button>
        ) : null}

        {/* Writes nothing to GitHub, so it carries `p.pr.preview` rather than
            `p.git_write`, and it renders itself away when the grant or the
            project's shared environments are missing. */}
        <PullRequestQaActions
          projectId={reference.projectId}
          prNumber={detail.number}
          disabled={isBusy}
        />

        {canMerge ? (
          <>
            <Select
              value={mergeMethod}
              onValueChange={(value) => {
                if (typeof value === "string") setMergeMethod(value as MergeMethodValue);
              }}
            >
              <SelectTrigger size="xs" className="w-40" aria-label="Merge method">
                <SelectValue>{MERGE_METHOD_LABELS[mergeMethod]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                {MERGE_METHOD_VALUES.map((value) => (
                  <SelectItem key={value} hideIndicator value={value}>
                    {MERGE_METHOD_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            <Button
              size="xs"
              disabled={isBusy || detail.mergeability === "conflicting"}
              onClick={() =>
                setPending({
                  action: "merge",
                  title: `Merge #${detail.number}?`,
                  description: `“${detail.title}” will be merged into ${detail.baseBranch} using ${MERGE_METHOD_LABELS[mergeMethod].toLowerCase()}. This writes to the base branch and cannot be undone from here.`,
                })
              }
            >
              Merge
            </Button>
          </>
        ) : null}

        {canReady ? (
          <Button size="xs" disabled={isBusy} onClick={() => run("ready")}>
            Ready for review
          </Button>
        ) : null}

        {canClose ? (
          <Button
            size="xs"
            variant="outline"
            disabled={isBusy}
            onClick={() =>
              setPending({
                action: "close",
                title: `Close #${detail.number}?`,
                description: `“${detail.title}” will be closed without merging. Anyone can reopen it, but it leaves everybody's list until they do.`,
              })
            }
          >
            Close
          </Button>
        ) : null}

        {canReopen ? (
          <Button size="xs" variant="outline" disabled={isBusy} onClick={() => run("reopen")}>
            Reopen
          </Button>
        ) : null}

        {detail.mergeability === "conflicting" && canMerge ? (
          <span className="text-[11px] text-muted-foreground">
            Resolve the conflicts with {detail.baseBranch} before merging.
          </span>
        ) : null}
      </div>

      <AlertDialog
        open={pending !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPending(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title ?? ""}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.description ?? ""}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-row-reverse gap-2 p-6 pt-2">
            <Button type="button" disabled={isBusy} onClick={confirmAndRun}>
              {pending?.action === "merge" ? "Merge" : "Close"}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setPending(null)}>
              Cancel
            </Button>
          </div>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
